import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {
  Icon, Link, ClampLines,
} from '@resy/ui-kitchen';
import IconCalendarDay from '../../../../../../../ui-kitchen/src/components/IconCalendarDay';
import pinSVG from '../../../../../../../ui-kitchen/src/svg/icons/ic_location_20x20.svg';
import getImage from '../../../AmexMarketingUtils/getAmexImage';
import {
  EVENT_SOURCE, getTeaser, DATE_TIME_FORMAT_EVENT, placeNotify, getHardcodedEventImageByRatio,
} from '../../ExperientialEvents';
import { sanitizeString } from '../../../../../../../ui-kitchen/src/utils/sanitizeString';
import { handleMktAnalytics } from '../../../AmexMarketingUtils/AmexUtils';
import { CTA_EVENT_NAME } from '../../../AmexMarketingUtils/constants';
import './AmexExperientialEventsCard.scss';

const AmexExperientialEventsCard = (
  {
    eventData,
    baseRequestUrl,
    soldoutLabel,
    isDesktop,
    timezoneMappings,
    isAuthenticated,
    isCarousel,
    $rootScope,
    selectedCoreSeats,
    updateParsedEventData,
    soldOutMessage,
    matchMedia,
    showAllEnabled,
    hideSoldOutDisplayFlag,
    simpleDisplayMode,
    displayVenueName,
    tabHeading,
    displaySection,
    Analytics,
    sectionName,
  }
) => {
  const {
    event_source: source,
    event_id: eventId,
    event_name: eventName,
    event_source: eventSource,
    logo,
    logo_alt: logoAlt,
    locations_text: locationsText,
    sold_out: isSoldout,
    event_url: link,
    cta_label: ctaLabel,
    passed_event: isPastEvent,
    teaser,
    timezone,
    showNotify,
    notify,
    error = {},
    venueName,
    venue_url: venueUrl,
    display_as_past_event_id: displayAsPastEvent,
    is_teaser: isTeaser,
    event_desc: eventDesc,
    display_tile: displayTile,
  } = eventData;

  const {
    error_message: errorMessage, field_names: fieldNames, date_info_message:
    dateInfoMessage, error_type: errorType, error_source: errorSource,
  } = error;

  const actionName = sectionName || 'Events And Experiences';

  const [isThinWidth, setIsThinWidth] = useState(false);

  const onResize = () => {
    const offsetWidthVal = window.document.getElementById('eventcard')?.offsetWidth;

    if (offsetWidthVal <= 226) {
      setIsThinWidth(true);
    } else {
      setIsThinWidth(false);
    }
  };

  useEffect(() => {
    const offsetWidthVal = window.document.getElementById('eventcard')?.offsetWidth;
    window.addEventListener('resize', onResize);

    if (offsetWidthVal <= 226) {
      setIsThinWidth(true);
    } else {
      setIsThinWidth(false);
    }

    return () => window.removeEventListener('resize', onResize);
  }, []);

  const placeNotifyAndUpdateEventDetails = async () => {
    const eventDetails = await placeNotify(eventData, selectedCoreSeats);
    updateParsedEventData(eventDetails, displaySection);
    let buttonState = '';
    if (isTeaser) {
      buttonState = 'TEASER';
    } else {
      buttonState = 'SOLDOUT';
    }
    const extraParam = {
      event_mode: buttonState,
      item_name: eventName,
      action_name: `${actionName} - Event Notify Click`,
      ...tabHeading && { tab_item: tabHeading },
    };
    handleMktAnalytics(Analytics?.event, CTA_EVENT_NAME, extraParam);
  };

  let showNotifyButton = false;
  if (((isTeaser && showNotify) || (!isTeaser && isSoldout && showNotify)) && !isPastEvent) {
    showNotifyButton = true;
  }

  const setNotify = () => {
    if (!isAuthenticated) {
      const successCallback = () => placeNotifyAndUpdateEventDetails;
      $rootScope.$broadcast('MODAL', 'signin', { successCallback });
    } else {
      placeNotifyAndUpdateEventDetails();
    }
  };

  const renderNotifyButton = () => {
    const { hasNotify } = notify;
    const notifyLabel = hasNotify ? 'Notify On' : 'Notify';
    return (
      <button
        type="button"
        className={cx('NotifyButton', { active: hasNotify, thincard: isThinWidth || hasNotify })}
        onClick={setNotify}
        aria-label={`${eventName} ${notifyLabel}`}
      >
        <Icon name="Bell" />
        {hasNotify
          ? <span>Notify On</span>
          : <span>Notify</span>}

      </button>
    );
  };

  const taggingVenue = () => {
    const extraParam = {
      item_name: venueName,
      action_name: `${actionName} - Venue Click`,
      ...tabHeading && { tab_item: tabHeading },
    };
    handleMktAnalytics(Analytics?.event, CTA_EVENT_NAME, extraParam);
  };
  const taggingEvent = () => {
    const extraParam = {
      item_name: eventName,
      action_name: `${actionName} - Event Click`,
      ...tabHeading && { tab_item: tabHeading },
    };
    handleMktAnalytics(Analytics?.event, CTA_EVENT_NAME, extraParam);
  };

  const renderLearnMore = () => (
    <Link
      target="_blank"
      rel="noopener noreferrer"
      href={link}
      key={eventId}
      className={cx('AmexExperientialEventsCard__card-cta', {
        notify: showNotifyButton,
      })}
      aria-label={`${ctaLabel} about ${eventName}`}
      onContextMenu={taggingEvent}
      onClick={taggingEvent}
    >
      {ctaLabel}
    </Link>
  );

  const renderPreview = () => {
    if (source === EVENT_SOURCE.RESY_API) {
      const { event_date: date } = eventData;
      return (<time dateTime={date} className="calendar-icon"><IconCalendarDay date={date} /></time>);
    }
    const {
      image_preview_text: previewContent,
    } = eventData;

    if (!previewContent) return null;
    const isPreviewDataEmpty = Object.keys(previewContent).length === 0 || Object.values(previewContent).every((value) => value.trim() === '');
    if (isPreviewDataEmpty) return null;

    const line1 = previewContent?.line_1;
    const line2 = previewContent?.line_2;
    const line3 = previewContent?.line_3;
    const previewLength = Object.keys(previewContent).length;

    const sanitizedLine1 = sanitizeString(line1);
    const sanitizedLine2 = sanitizeString(line2);
    const sanitizedLine3 = sanitizeString(line3);

    return (
      <p
        className={`AmexExperientialEventsCard__badge cols-${previewLength}`}
        aria-hidden="true"
      >
        {line1 && (
          <span
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitizedLine1,
            }}
          />
        )}
        {line2 && (
          <span
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitizedLine2,
            }}
          />
        )}
        {line3 && (
          <span
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitizedLine3,
            }}
          />
        )}
      </p>
    );
  };

  const renderLogo = () => {
    if (!logo) return null;

    return (
      <div
        className="logo-icon"
        role="img"
        aria-label={logoAlt}
        style={{
          backgroundImage: `url(${baseRequestUrl}Experiential/eventsImages/logos/${logo})`,
        }}
      />
    );
  };

  const getFormattedDateTime = () => {
    if (displayTile) return null;
    if (source === EVENT_SOURCE.HARDCODED || source === EVENT_SOURCE.VENUE_API) {
      const { formatted_display_date: formattedDisplayDate } = eventData;
      if (!formattedDisplayDate) return null;
      return (
        <p className="date">
          {formattedDisplayDate}
        </p>
      );
    }
    const {
      event_date: date, event_time: time, event_end_time: endTime, event_type: eventType,
    } = eventData;
    const endTimeFormatted = (endTime && eventType !== 1) ? ` - ${moment(endTime, 'HH:mm').format('LT')}` : '';
    const startToEnd = `${moment(`${date} ${time}`, DATE_TIME_FORMAT_EVENT).format('llll')}${endTimeFormatted}`;

    if (!endTimeFormatted) {
      return <p className="date"><span>{startToEnd}</span></p>;
    }

    return (
      <div className="date">
        <ClampLines
          text={startToEnd}
          id={`event-card-${date}-${startToEnd}`}
          lines={2}
          buttons={false}
        />
      </div>
    );
  };

  const renderLocation = () => {
    if (locationsText) {
      return (
        <p className="neighborhood">
          <Icon component={pinSVG} iconClass="pin" role="presentation" />
          {locationsText}
        </p>
      );
    }

    return null;
  };
  const renderImage = () => {
    if (source === EVENT_SOURCE.RESY_API || source === EVENT_SOURCE.VENUE_API) {
      const { responsive_images: images, image_preview: imagePreview } = eventData;
      const image = getImage(images, imagePreview, isDesktop);
      return (
        <div
          className="image"
          style={{
            backgroundImage: `url(${image})`,
          }}
        >
          <div
            className={cx('skeleton-animation', {
              'no-skeleton': image || image === null,
            })}
          />
        </div>
      );
    }
    const { responsive_images: images } = eventData;
    const image = getHardcodedEventImageByRatio(images, matchMedia);
    return (
      <div
        className="image"
        style={{
          backgroundImage: `url(${baseRequestUrl}Experiential/eventsImages/${image})`,
        }}
      >
        <div
          className={cx('skeleton-animation', {
            'no-skeleton': image || image === null,
          })}
        />
      </div>
    );
  };

  const renderTeaser = () => {
    const teaserCopy = getTeaser(teaser, timezone, timezoneMappings);
    if (!teaserCopy) return false;
    if (source === EVENT_SOURCE.RESY_API && !isSoldout) return false;
    return (
      <div className="AmexExperientialEventsCard__teaser">
        <p>{teaserCopy}</p>
      </div>
    );
  };

  const renderSoldOutMessage = () => (
    <div className="AmexExperientialEventsCard__soldout">
      <p>{soldOutMessage}</p>
    </div>
  );

  const renderVal = (name, displayValue) => (
    <p>
      <strong>{name}</strong>
      {' '}
      -
      {' '}
      {displayValue}
    </p>
  );
  const renderDateInfoMessage = () => {
    const {
      display_start_date_time: displayStartDate,
      display_end_date_time: displayEndDate, venue_id: venueId,
    } = eventData;
    const eventType = `${eventSource.toUpperCase()} EVENT`;
    return (
      <div className="AmexExperientialEventsCard__errorContainer">
        <div className="errorinfo dateerror">
          {renderVal('EVENT TYPE', eventType)}
          {renderVal('DISPLAY START DATE/TIME', displayStartDate)}
          {renderVal('DISPLAY END DATE/TIME', displayEndDate)}
          {(source === EVENT_SOURCE.VENUE_API) && renderVal('VENUE ID', venueId)}
          {renderVal('ERROR MESSAGE', dateInfoMessage)}
        </div>
      </div>
    );
  };
  const renderErrorData = () => {
    const { event_slug: eventSlug, venue_id: venueId } = eventData;
    let displayValue = '';
    if (eventSource === EVENT_SOURCE.HARDCODED) {
      displayValue = renderVal('EVENT NAME', eventName);
    } else if (eventSource === EVENT_SOURCE.RESY_API) {
      displayValue = renderVal('EVENT SLUG', eventSlug);
    } else if (eventSource === EVENT_SOURCE.VENUE_API) {
      displayValue = renderVal('VENUE ID', venueId);
    }

    const eventType = `${eventSource.toUpperCase()} EVENT`;
    return (
      <div className="AmexExperientialEventsCard__errorContainer">
        <div className="errorinfo fielderror">
          {displayValue}
          {renderVal('EVENT TYPE', eventType)}
          {renderVal('EVENT LABELS', eventData?.event_labels?.toString())}
          {renderVal('IMPACTED FIELD NAMES', fieldNames)}
          {renderVal('ERROR TYPE', errorType)}
          {renderVal('ERROR SOURCE', errorSource)}
          {renderVal('ERROR MESSAGE', errorMessage)}
        </div>
      </div>
    );
  };

  const renderEventLabel = () => {
    const labels = eventData?.event_labels;
    const index = eventData?.priority_index;
    if (index || labels) {
      return (
        <div className="AmexExperientialEventsCard__errorContainer">
          <div className="errorinfo dateerror">
            {renderVal('LABELS', labels?.toString())}
            {'priority_index' in eventData ? renderVal('PRIORITY INDEX', index) : ''}
          </div>
        </div>
      );
    }
    return null;
  };

  const renderCardContent = () => {
    const sanitizeEventName = sanitizeString(eventName);
    return (
      <>
        <div className="info">
          {link ? (
            <h3 className="event-name">
              <Link
                target="_blank"
                rel="noopener noreferrer"
                href={link}
                key={`${eventName}-${eventId}`}
                aria-label={`${ctaLabel} about ${eventName}`}
                variant="article"
                onContextMenu={taggingEvent}
                onClick={taggingEvent}
                dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
                  __html: sanitizeEventName,
                }}
              />
            </h3>
          ) : (
            <h3
              className="event-name"
              dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
                __html: sanitizeEventName,
              }}
            />
          )}

          {displayTile
            && (
            <p className="AmexExperientialEventsCard__desc">
              {eventDesc}
            </p>
            )}

          {(source === EVENT_SOURCE.RESY_API && displayVenueName && venueName) && (
            <h4 className="venue-name">
              <Link
                target="_blank"
                rel="noopener noreferrer"
                href={venueUrl}
                key={`${eventName}-${eventId}`}
                aria-label={`${eventName} venue`}
                onContextMenu={taggingVenue}
                onClick={taggingVenue}
                className="link-venue-name"
              >
                {venueName}
              </Link>
            </h4>
          )}
          {(source === EVENT_SOURCE.RESY_API && !isTeaser && isSoldout && !hideSoldOutDisplayFlag) && (
            <p className="event-soldout">
              {soldoutLabel}
            </p>
          )}
          {(source !== EVENT_SOURCE.RESY_API && isSoldout && !hideSoldOutDisplayFlag) && (
            <p className="event-soldout">
              {soldoutLabel}
            </p>
          )}

          {renderPreview()}
          {getFormattedDateTime()}
          {renderLocation()}
        </div>
        <div className="AmexExperientialEventsCard__card-top">
          {renderImage()}
          {renderLogo()}
        </div>
        {isTeaser}
        <div className="AmexExperientialEventsCard__card-footer">
          {!simpleDisplayMode && isTeaser && renderTeaser()}
          {!simpleDisplayMode && isSoldout && showNotify && !isTeaser && renderSoldOutMessage()}
          <div
            className={cx('AmexExperientialEventsCard__card-buttons', {
              thincard: isThinWidth || notify?.hasNotify,
            })}
          >
            {link && renderLearnMore()}
            {!simpleDisplayMode && isSoldout && showNotifyButton && renderNotifyButton()}
          </div>
        </div>
        {showAllEnabled && renderEventLabel()}
        {dateInfoMessage && renderDateInfoMessage()}
      </>
    );
  };

  return isCarousel && matchMedia.md ? (
    <div className="AmexExperientialEventsCard">
      <article
        id="eventcard"
        className={cx('AmexExperientialEventsCard__card', {
          'with-link': link,
          disabled: isPastEvent && !displayAsPastEvent,
        })}
      >
        {!errorMessage && renderCardContent()}
        {errorMessage && renderErrorData()}
      </article>
    </div>
  ) : (
    <li className="AmexExperientialEventsCard">
      <article
        id="eventcard"
        className={cx('AmexExperientialEventsCard__card', {
          'with-link': link,
          disabled: isPastEvent && !displayAsPastEvent,
        })}
      >
        {!errorMessage && renderCardContent()}
        {errorMessage && renderErrorData()}
      </article>
    </li>
  );
};

AmexExperientialEventsCard.propTypes = {
  eventData: PropTypes.shape({
    event_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    event_name: PropTypes.string.isRequired,
    display_as_past_event_id: PropTypes.number,
    image: PropTypes.string,
    logo: PropTypes.string,
    logo_alt: PropTypes.string,
    event_date_time: PropTypes.string.isRequired,
    display_start_date_time: PropTypes.string.isRequired,
    display_end_date_time: PropTypes.string.isRequired,
    image_preview_text: PropTypes.shape({
      line_1: PropTypes.string,
      line_2: PropTypes.string,
      line_3: PropTypes.string,
    }),
    locations_text: PropTypes.string,
    sold_out: PropTypes.bool,
    event_url: PropTypes.string.isRequired,
    cta_label: PropTypes.string.isRequired,
    formatted_display_date: PropTypes.string,
    event_source: PropTypes.string.isRequired,
    responsive_images: PropTypes.shape({
      urls: PropTypes.shape(),
      file_names: PropTypes.arrayOf(PropTypes.string),
      aspect_ratios: PropTypes.shape(),
    }).isRequired,
    image_preview: PropTypes.string,
    event_date: PropTypes.string.isRequired,
    event_time: PropTypes.string.isRequired,
    event_end_time: PropTypes.string.isRequired,
    event_type: PropTypes.number.isRequired,
    passed_event: PropTypes.bool,
    teaser: PropTypes.shape({}),
    timezone: PropTypes.string,
    showNotify: PropTypes.bool,
    notify: PropTypes.shape({
      hasNotify: PropTypes.bool.isRequired,
    }),
    event_slug: PropTypes.string,
    venue_id: PropTypes.string,
    venueName: PropTypes.bool,
    venue_url: PropTypes.string,
    error: PropTypes.shape({
      error_message: PropTypes.string,
      field_names: PropTypes.string,
      date_info_message: PropTypes.string,
      error_type: PropTypes.string,
      error_source: PropTypes.string,
    }),
    event_labels: PropTypes.arrayOf(PropTypes.shape({})),
    priority_index: PropTypes.number,
    is_teaser: PropTypes.bool,
    event_desc: PropTypes.string,
    display_tile: PropTypes.bool,
  }),
  baseRequestUrl: PropTypes.string.isRequired,
  soldoutLabel: PropTypes.string.isRequired,
  isDesktop: PropTypes.bool.isRequired,
  timezoneMappings: PropTypes.shape({}).isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  isCarousel: PropTypes.bool,
  selectedCoreSeats: PropTypes.number.isRequired,
  $rootScope: PropTypes.shape({
    $broadcast: PropTypes.func.isRequired,
  }).isRequired,
  updateParsedEventData: PropTypes.func.isRequired,
  soldOutMessage: PropTypes.string,
  actionConfig: PropTypes.shape({
    notify: PropTypes.string.isRequired,
    event_click: PropTypes.string.isRequired,
  }),
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    ipadLandscape: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  showAllEnabled: PropTypes.bool.isRequired,
  hideSoldOutDisplayFlag: PropTypes.bool,
  simpleDisplayMode: PropTypes.bool,
  displayVenueName: PropTypes.bool,
  tabHeading: PropTypes.string,
  displaySection: PropTypes.string.isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func.isRequired,
  }).isRequired,
  sectionName: PropTypes.string,
};

AmexExperientialEventsCard.defaultProps = {
  eventData: {
    image: '',
    logo: '',
    logo_alt: '',
    locations_text: '',
    passed_event: false,
    teaser: {},
    image_preview: '',
    image_preview_text: {},
    sold_out: false,
    formatted_display_date: '',
    timezone: '',
    showNotify: false,
    error: {
      error_message: '',
      field_names: '',
      date_info_message: '',
      error_type: '',
      error_source: '',
    },
    display_as_past_event_id: null,
    display_tile: false,
  },
  isCarousel: false,
  soldOutMessage: '',
  hideSoldOutDisplayFlag: false,
  simpleDisplayMode: false,
  displayVenueName: false,
  actionConfig: undefined,
  tabHeading: undefined,
  sectionName: null,
};

export default AmexExperientialEventsCard;
